import React, { useState } from 'react';
import { Box, Button, Checkbox, Chip, IconButton, styled, Tooltip, Typography } from '@mui/material';
import { Add, DeleteOutline, Edit, Inventory2Outlined } from '@mui/icons-material';
import { getLooseProducts, isLooseProducts } from 'helpers/budgetItems';
import { moneyFormat } from 'helpers/moneyFormat';
import { ProcedurePackage } from 'types/procedure';
import { usePermissionRules } from 'hooks/permissionRules';
import { useNewSurgerie } from '../../hooks/useNewSurgerie';
import EditPackageValueDialog from '../../dialog/EditPackageValueDialog';
import AddPackageDialog from '../../dialog/AddPackageDialog';
import AddProductDialog from '../../dialog/AddProductDialog';
import LooseProductsPanel from './LooseProductsPanel';
import {
  CardBody,
  GuidedCard,
  GuidedHeader,
  HelperNote,
  ItemRow,
  palette,
  SoftPanel,
} from '../../components/GuidedSurgerie';

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const PackageHeader = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  gap: 12,
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const PackageActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 8,
});

const ProductList = styled('div')({
  marginTop: 12,
  borderTop: `1px solid ${palette.border}`,
});

const SurgerieFifthStep: React.FC = () => {
  const { surgerie, handleChangePackages } = useNewSurgerie();
  const [selectedPkg, setSelectedPkg] = useState<ProcedurePackage | null>(null);
  const [addingPackage, setAddingPackage] = useState(false);
  const [addingProduct, setAddingProduct] = useState(false);
  const { checkPermission } = usePermissionRules();
  const canAdd = checkPermission('surgeries.new.change-package-value');
  const packages = surgerie.packages.filter(item => !isLooseProducts(item));
  const looseProducts = getLooseProducts(surgerie.packages);

  function handleChangeBonificate(pkg: ProcedurePackage) {
    if (!checkPermission('surgeries.new.bonificate-package')) {
      return;
    }

    const updatedPackages = surgerie.packages.map(p => {
      if (p.id === pkg.id) {
        return {
          ...p,
          package: {
            ...p.package,
            sale_price: p.package?.sale_price === 0 ? pkg.package?.sale_price_min || 0 : 0,
            id: p.package?.id || 0,
          },
        } as ProcedurePackage;
      }
      return p;
    });

    handleChangePackages(updatedPackages as ProcedurePackage[]);
  }

  function handleRemovePackage(pkg: ProcedurePackage) {
    handleChangePackages(surgerie.packages.filter(item => item.id !== pkg.id));
  }

  return (
    <GuidedCard>
      {canAdd && selectedPkg && (
        <EditPackageValueDialog onExited={() => setSelectedPkg(null)} selectedPkg={selectedPkg} />
      )}
      {addingPackage && <AddPackageDialog onExited={() => setAddingPackage(false)} />}
      {addingProduct && <AddProductDialog onExited={() => setAddingProduct(false)} />}

      <GuidedHeader
        step={5}
        title="Produtos e pacotes"
        description="Revise os pacotes sugeridos, adicione ou remova itens e personalize valores quando necessário."
        aside={
          <Box display="flex" alignItems="center" gap={1}>
            <Chip color="primary" size="small" label={`${packages.length} pacotes · ${looseProducts.length} avulsos`} />
            {canAdd && (
              <>
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setAddingPackage(true)}>
                  Pacote
                </Button>
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setAddingProduct(true)}>
                  Produto
                </Button>
              </>
            )}
          </Box>
        }
      />

      <CardBody>
        <Container>
          {packages.length === 0 && looseProducts.length === 0 && (
            <HelperNote>
              <Inventory2Outlined fontSize="small" />
              <Typography variant="caption">
                Nenhum pacote ou produto neste orçamento.{canAdd && ' Use os botões acima para adicionar.'}
              </Typography>
            </HelperNote>
          )}

          {packages.map(pkg => (
            <SoftPanel key={pkg.id || pkg.package?.id}>
              <PackageHeader>
                <div>
                  <Typography variant="body2" fontSize={16} fontWeight={700} color={palette.ink}>
                    {pkg.package?.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {pkg.package?.products?.length || 0} produtos inclusos
                  </Typography>
                </div>

                <Typography
                  variant="body2"
                  color={pkg.package?.sale_price === 0 ? 'textSecondary' : 'success.main'}
                  fontWeight={700}
                >
                  {moneyFormat(pkg.package?.sale_price)}
                </Typography>

                <PackageActions>
                  <IconButton
                    disabled={!checkPermission('surgeries.new.change-package-value')}
                    size="small"
                    onClick={() => setSelectedPkg(pkg)}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <Tooltip title="Remover pacote">
                    <IconButton size="small" color="error" disabled={!canAdd} onClick={() => handleRemovePackage(pkg)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Checkbox
                      size="small"
                      disabled={!checkPermission('surgeries.new.bonificate-package')}
                      checked={pkg.package?.sale_price === 0}
                      onChange={() => handleChangeBonificate(pkg)}
                    />
                    <Typography variant="caption">Bonificar</Typography>
                  </Box>
                </PackageActions>
              </PackageHeader>

              {pkg.package && pkg.package.products.length > 0 && (
                <ProductList>
                  {pkg.package?.products?.map(product => (
                    <ItemRow key={product.product?.id || product.product_id}>
                      <Typography variant="body2">{product.product?.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Quantidade
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {product.quantity} {product.product?.unit}
                      </Typography>
                    </ItemRow>
                  ))}
                </ProductList>
              )}
            </SoftPanel>
          ))}

          <LooseProductsPanel />
        </Container>
      </CardBody>
    </GuidedCard>
  );
};

export default SurgerieFifthStep;
